import React, { Component } from 'react';
import { Card, Container, Row, Col } from 'react-bootstrap';
import {
  listMissions,
  getMission,
  listCells,
  listDependsOn,
  getCell,
  upsertCell,
  removeCell,
  addDependsOnEdge,
  removeDependsOnEdge,
} from '../../domain/model';
import MissionGraph, { edgeRfId, newCellPosition } from './missionGraph';
import DeleteCellDialog from './deleteCellDialog';
import { classifyCellObjects, describeCellObjects } from '../../domain/cellObjects';
import CellPanel from './cellPanel';
import CellDetailSurface from '../cell';
import MissionPanel from './missionPanel';
import DependencyPanel from './dependencyPanel';
import WidgetStatus from '../../../common/widgetStatus';
import './cells.css';

/**
 * CellsSurface — the core surface of Mission Control.
 *
 * Layout:
 *   [ Card: MissionGraph (flex 1) ][ Card: detail panel (right dock) ]
 *
 * The detail panel is ALWAYS rendered while a mission is active and shows one of three
 * views depending on the selection: a selected cell → CellPanel, a selected dependency
 * edge → DependencyPanel, nothing selected → MissionPanel (mission summary). Deselecting
 * therefore never hides the panel — it falls back to the Mission view.
 *
 * The "+ Cell" button and add-cell modal have been moved to Shell, which already
 * has access to client, selection.missionId, and store.templates. CellsSurface
 * renders only the graph canvas card and the detail panel card.
 *
 * Selection state flows through the shell's selection/onSelect props so that
 * surface-switching preserves selection. The selection shape is:
 *   { missionId: string, cellId: string|null, edge: object|null }
 * Only one of cellId / edge is ever set.
 *
 * Every write here is INCREMENTAL: it calls the client, then applies a pure
 * store reducer through onApplyLocal — never onReload. The mission graph updates
 * in place (no re-fetch, no re-layout, no loading spinner). The editing handlers
 * are stable instance methods that read the current mission id from props at call
 * time, so the MissionGraph prop chain stays referentially stable across renders.
 *
 * Props:
 *   store        — model store (from MissionsNode state)
 *   client       — MissionsClient instance
 *   selection    — { missionId?, cellId?, edge? } | null
 *   onSelect     — (item) => void
 *   onApplyLocal — (updater: store => store) => void  — incremental local store update
 */
class CellsSurface extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    // Local UI state: the Mission panel's × collapses the whole detail dock for more graph room.
    // Selecting a cell/edge re-opens it; a plain deselect (pane click) does NOT.
    // `deleting` is the whole of the delete dialog's state: which cell, what goes with it, how far
    // along, and what went wrong. Null when no delete is being confirmed or run.
    this.state = { panelHidden: false, openCellId: null, deleting: null };
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  // ---------------------------------------------------------------------------
  // Mission resolution — prefer the selected mission, fall back to the first one
  // ---------------------------------------------------------------------------

  _missionId() {
    const { store, selection } = this.props;
    const missions = store ? listMissions(store) : [];
    return (selection && selection.missionId) || (missions.length > 0 ? missions[0].id : null);
  }

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  // Select a cell (clearing any edge selection). A null cellId — e.g. a pane click — fully deselects,
  // which falls back to the Mission panel.
  handleSelectCell = (cellId) => {
    const { onSelect } = this.props;
    // A real selection re-opens a collapsed dock; a pane-click deselect (cellId null) leaves it as-is.
    if (cellId && this.state.panelHidden) this.setState({ panelHidden: false });
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: cellId || null, edge: null });
  };

  // Select a dependency edge (clearing any cell selection).
  handleSelectEdge = (edge) => {
    const { onSelect } = this.props;
    if (edge && this.state.panelHidden) this.setState({ panelHidden: false });
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: null, edge: edge || null });
  };

  // Close the cell / dependency panel — deselect everything, which falls back to the Mission panel.
  handleClosePanel = () => {
    const { onSelect } = this.props;
    if (onSelect) onSelect({ missionId: this._missionId(), cellId: null, edge: null });
  };

  // Switch the active mission (the Mission panel's switcher). Clears any cell/edge selection.
  // ---------------------------------------------------------------------------
  // Incremental edits — client write, then onApplyLocal(pure reducer)
  // ---------------------------------------------------------------------------

  // Wire a dependency: dependent depends on prerequisite (the arrow is drawn dependent → prerequisite,
  // arrowhead on the prerequisite).
  handleAddDependency = async (dependentId, prerequisiteId) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    // Never create a DUPLICATE dependency. The local reducer dedups by (from,to), but the server write
    // does not — so re-adding an existing pair would commit a second edge that surfaces on the next
    // reload. Bail out entirely (no server write) when the edge already exists.
    const existing = store ? listDependsOn(store, missionId) : [];
    if (existing.some(e => e.from_id === dependentId && e.to_id === prerequisiteId)) return;
    try {
      const edge = await client.addDependsOn(missionId, dependentId, prerequisiteId);
      if (onApplyLocal) onApplyLocal(s => addDependsOnEdge(s, missionId, edge));
    } catch (_e) {
      // Ignore; the graph reflects server state on the next genuine reload.
    }
  };

  // Add a DEPENDENT cell — a new cell that depends on this one (this cell is the prerequisite) — and
  // wire "new depends on this" in one gesture. Both client calls are awaited first, then the new cell
  // AND its depends_on edge are applied to the store in a SINGLE local update so the node and its edge
  // appear together in one refresh — not as two separate pops. The new cell is placed to the RIGHT of
  // its parent (and that x/y persisted) so it lands beside it rather than at the dagre origin; parentPos
  // is the parent's current rendered position, threaded from CellNode.
  handleAddDependent = async (cellId, parentPos) => {
    const { client, onApplyLocal, onSelect } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    try {
      // The parent keeps its place: see pinCell.
      await this.pinCell(cellId, parentPos);
      const pos = newCellPosition(parentPos, 'dependent');
      const dependent = await client.addCell(missionId, {
        title: 'New cell',
        // New cells default both structural choices to inherit-from-mission.
        workspace_mode: 'inherit_from_mission',
        cell_workflow_mode: 'inherit_from_mission',
        ...(pos || {}),
      });
      // new cell (dependent) depends on cellId (prerequisite): from_id = new, to_id = this.
      const edge = await client.addDependsOn(missionId, dependent.element_id, cellId);
      if (onApplyLocal) {
        onApplyLocal(s => addDependsOnEdge(upsertCell(s, missionId, dependent), missionId, edge));
      }
      if (onSelect) onSelect({ missionId, cellId: dependent.element_id, edge: null });
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
  };

  // Add a PREREQUISITE cell — a new cell that this one depends on (the new cell is the prerequisite) —
  // and wire "this depends on new" in one gesture. Mirrors handleAddDependent but with the dependency
  // pointing the other way and the new cell placed to the LEFT of its parent; the new cell + its edge
  // are applied in a SINGLE local update.
  handleAddPrerequisite = async (cellId, parentPos) => {
    const { client, onApplyLocal, onSelect } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    try {
      await this.pinCell(cellId, parentPos);
      const pos = newCellPosition(parentPos, 'prerequisite');
      const prerequisite = await client.addCell(missionId, {
        title: 'New cell',
        // New cells default both structural choices to inherit-from-mission.
        workspace_mode: 'inherit_from_mission',
        cell_workflow_mode: 'inherit_from_mission',
        ...(pos || {}),
      });
      // cellId (dependent) depends on the new cell (prerequisite): from_id = this, to_id = new.
      const edge = await client.addDependsOn(missionId, cellId, prerequisite.element_id);
      if (onApplyLocal) {
        onApplyLocal(s => addDependsOnEdge(upsertCell(s, missionId, prerequisite), missionId, edge));
      }
      if (onSelect) onSelect({ missionId, cellId: prerequisite.element_id, edge: null });
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
  };

  /**
   * Pin a cell where it is currently drawn, if nothing has pinned it yet.
   *
   * A cell with no persisted x/y gets a DAGRE slot, and dagre re-runs whenever any cell in the graph
   * lacks coordinates — so an unpinned cell is re-slotted every time the graph changes shape. Adding
   * a neighbour changes the shape, which is why adding one to the root appeared to MOVE the root: it
   * had never been dragged, so it had no coordinates of its own to keep.
   *
   * Writing the position it is already drawn at is visually a no-op and makes it stay there. Called
   * with the parent's live rendered position, which the node hands to the add gesture anyway.
   */
  pinCell = async (cellId, position) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !position || position.x == null || position.y == null) return;
    const cell = store ? getCell(store, missionId, cellId) : null;
    if (!cell) return;
    const props = cell.props || {};
    if (props.x != null && props.y != null) return;   // already pinned — leave it exactly as it is
    const merged = { ...cell, props: { ...props, x: position.x, y: position.y } };
    if (onApplyLocal) onApplyLocal(s => upsertCell(s, missionId, merged));
    try {
      await client.setCellProps(missionId, cellId, { x: position.x, y: position.y });
    } catch (_e) {
      // Ignore; the optimistic local position stands until the next reload.
    }
  };

  // Persist a dragged cell's new position (optimistic local upsert + server write) so the graph never
  // re-layouts that cell on later edits.
  handleMoveCell = async (cellId, position) => {
    const { client, onApplyLocal, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !position) return;
    const cell = store ? getCell(store, missionId, cellId) : null;
    if (!cell) return;
    const merged = { ...cell, props: { ...(cell.props || {}), x: position.x, y: position.y } };
    if (onApplyLocal) onApplyLocal(s => upsertCell(s, missionId, merged));
    try {
      await client.setCellProps(missionId, cellId, { x: position.x, y: position.y });
    } catch (_e) {
      // Ignore; the optimistic local position stands until the next reload.
    }
  };

  /**
   * Remove a cell — the vertex, and everything the cell owns in the session namespace.
   *
   * A cell that owns nothing goes straight away: the only thing lost is an empty cell, and a dialog
   * to say so is furniture. A cell that owns a thread or an artifact is confirmed first, because
   * what goes with it is work an agent did and the record of why, and none of it comes back.
   */
  handleRemoveCell = async (cellId) => {
    const { client, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId) return;
    const groups = classifyCellObjects(client.listCellObjectAliases(missionId, cellId));
    if (groups.total === 0) return this._deleteCell(cellId);
    const cell = store ? getCell(store, missionId, cellId) : null;
    this.setState({
      deleting: {
        cellId,
        title: (cell && cell.props && cell.props.title) || cellId,
        summary: describeCellObjects(groups),
        total: groups.total,
        progress: null,
        error: null,
      }
    });
  };

  // The confirmed path: the cell's objects first, then the vertex.
  //
  // That order is the recoverable one. Delete the vertex first and a failure halfway through the
  // objects leaves them with nothing pointing at them — invisible, and findable only by knowing a
  // dead cell's id. This way a failure leaves the CELL, so the graph still shows what is left and
  // pressing delete again resumes; which is also why a partial failure stops rather than removing
  // the vertex anyway.
  _deleteCellWithObjects = async () => {
    const { client } = this.props;
    const missionId = this._missionId();
    const { cellId } = this.state.deleting;
    // Seeded with the count already known, so the alert is never a frame of "0/0". The first real
    // event lands in the same tick and adds the alias.
    this.setState(state => ({
      deleting: { ...state.deleting, progress: { done: 1, total: state.deleting.total } }
    }));
    let result;
    try {
      result = await client.deleteCellObjects(missionId, cellId, this._recordDeleteProgress);
    } catch (e) {
      return this._deleteFailed(e && e.message ? e.message : 'Could not delete this cell.');
    }
    if (result.failed.length > 0) {
      return this._deleteFailed(
        `Kept the cell: ${result.failed.length} of ${result.failed.length + result.deleted.length} ` +
        'objects could not be deleted. Try again.'
      );
    }
    await this._deleteCell(cellId);
    if (this._mounted) this.setState({ deleting: null });
  };

  // One event per object, as it starts — which is the tense the alert is written in.
  _recordDeleteProgress = (progress) => {
    if (!this._mounted) return;
    this.setState(state => (state.deleting ? { deleting: { ...state.deleting, progress } } : null));
  };

  _deleteFailed = (message) => {
    if (!this._mounted) return;
    this.setState(state => (state.deleting ? { deleting: { ...state.deleting, error: message } } : null));
  };

  // The vertex itself, and the local reflections of its removal.
  _deleteCell = async (cellId) => {
    const { client, onApplyLocal, onSelect, selection } = this.props;
    const missionId = this._missionId();
    // Delete UNCONDITIONALLY — no if_rev guard. The store's cell.rev goes stale the moment the cell is
    // edited (set_vertex_props is itself unconditional and _applyLocalProps keeps the old rev) or the
    // heartbeat updates the cell server-side; a stale if_rev would make the server reject the delete
    // while the optimistic local removal still runs, so the cell "comes back" on the next reload.
    try {
      await client.deleteCell(missionId, cellId);
    } catch (_e) {
      // Ignore; reflected on next reload.
    }
    if (onApplyLocal) onApplyLocal(s => removeCell(s, missionId, cellId));
    if (selection && selection.cellId === cellId && onSelect) {
      onSelect({ missionId, cellId: null, edge: null });
    }
  };

  // Remove a dependency edge (identified by element_id; located in the store by from/to). When the
  // removed edge is the selected one, deselect so the panel falls back to the Mission view.
  handleRemoveEdge = async (edge) => {
    const { client, onApplyLocal, onSelect, selection, store } = this.props;
    const missionId = this._missionId();
    if (!client || !missionId || !edge) return;
    // Delete EVERY server edge for this (from,to) pair — not just the one passed — so any pre-existing
    // duplicates are cleaned up in a single remove and don't resurface on the next reload.
    const all = store
      ? listDependsOn(store, missionId).filter(e => e.from_id === edge.from_id && e.to_id === edge.to_id)
      : [];
    const targets = all.length ? all : [edge];
    for (const e of targets) {
      try {
        // Unconditional delete (no if_rev) — same stale-rev reason as handleRemoveCell.
        if (e.element_id) await client.deleteEdge(missionId, e.element_id);
      } catch (_e) {
        // Ignore; reflected on next reload.
      }
    }
    if (onApplyLocal) onApplyLocal(s => removeDependsOnEdge(s, missionId, edge.from_id, edge.to_id));
    const sel = selection && selection.edge;
    if (sel && sel.from_id === edge.from_id && sel.to_id === edge.to_id && onSelect) {
      onSelect({ missionId, cellId: null, edge: null });
    }
  };

  handleOpenCell = (cellId) => this.setState({ openCellId: cellId });

  handleBackToMission = () => this.setState({ openCellId: null });

  render() {
    const { store, client, selection, onApplyLocal, canEdit } = this.props;
    const { deleting } = this.state;

    // Resolve mission id: prefer the one in selection, fall back to first available
    const missions = store ? listMissions(store) : [];
    const selMissionId = this._missionId();
    // Every graph gesture that writes is passed as a handler, and MissionGraph draws an affordance
    // only for a handler it was given (see CellNode's toolbar, and GraphCanvas's "absent props keep
    // the canvas static"). So withholding them here is the whole of read-only on the canvas: no
    // toolbar buttons, no connectable handles, no draggable nodes. `canEdit` defaults to true so an
    // embedding that does not express the seam behaves as it did.
    const editable = !!selMissionId && canEdit !== false;
    const selCellId = (selection && selection.cellId) || null;
    const selEdge = (selection && selection.edge) || null;

    // Cells and dependency edges for the active mission
    const cells = (store && selMissionId) ? listCells(store, selMissionId) : [];
    const dependsOnEdges = (store && selMissionId) ? listDependsOn(store, selMissionId) : [];

    // Selected cell vertex / active mission vertex / selected edge id
    const selectedCell = (store && selMissionId && selCellId)
      ? getCell(store, selMissionId, selCellId)
      : null;
    const mission = (store && selMissionId) ? getMission(store, selMissionId) : null;
    const selectedEdgeId = selEdge ? edgeRfId(selEdge) : null;

    // The detail dock shows unless the user collapsed it (Mission panel ×) AND nothing is selected.
    // A live cell/edge selection always shows its panel.
    const showPanel = !!(selectedCell || selEdge || !this.state.panelHidden);

    // Drilling into a cell takes the whole surface. The mission map answers "what is the shape of
    // this work"; a cell answers "what is happening inside this piece of it", and that needs a tree
    // and a transcript rather than the dock beside the graph.
    const openCell = (store && selMissionId && this.state.openCellId)
      ? getCell(store, selMissionId, this.state.openCellId)
      : null;
    if (openCell) {
      return (
        <CellDetailSurface
          key={openCell.element_id}
          cell={openCell}
          missionId={selMissionId}
          client={client}
          workflows={store && store.templates ? store.templates.workflows : []}
          workspaces={store && store.templates ? store.templates.workspaces : []}
          onBack={this.handleBackToMission}
          // The cell screen edits a draft's details and workflow now, so it needs the same local
          // reflection the dock has: a write shows up without waiting for a reload, and the graph
          // behind it agrees with the panel in front of it.
          onApplyLocal={onApplyLocal}
          canEdit={editable}
        />
      );
    }

    if (missions.length === 0) {
      // Read-only gets the fact without the instruction: the button the sentence names is not in
      // the header, and telling somebody to press a control that is not there reads as a broken
      // widget rather than as an empty one.
      return (
        <WidgetStatus variant="info">
          {canEdit === false
            ? 'No missions yet.'
            : 'No missions yet. Use the New mission button in the widget header to create one.'}
        </WidgetStatus>
      );
    }

    return (
      <Container fluid className="mc-cells-container">
        <Row className="mc-grid-row">
          {/* Mission graph — full-bleed canvas in a flat card */}
          <Col className="d-flex flex-column">
            <Card className="mc-graph-card">
              <Card.Body className="mc-card-flush">
                <MissionGraph
                  key={selMissionId}
                  cells={cells}
                  dependsOnEdges={dependsOnEdges}
                  selectedId={selCellId}
                  selectedEdgeId={selectedEdgeId}
                  workspaces={store && store.templates ? store.templates.workspaces : []}
                  onSelect={this.handleSelectCell}
                  onOpen={this.handleOpenCell}
                  onSelectEdge={this.handleSelectEdge}
                  onAddDependency={editable ? this.handleAddDependency : undefined}
                  onAddPrerequisite={editable ? this.handleAddPrerequisite : undefined}
                  onAddDependent={editable ? this.handleAddDependent : undefined}
                  onRemoveCell={editable ? this.handleRemoveCell : undefined}
                  onRemoveEdge={editable ? this.handleRemoveEdge : undefined}
                  onMoveCell={editable ? this.handleMoveCell : undefined}
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Detail dock — shown unless collapsed via the Mission panel's ×. One of: the selected
              cell (CellPanel), the selected dependency edge (DependencyPanel), or the mission summary
              (MissionPanel) when nothing is selected. */}
          {showPanel && (
          <Col className="d-flex flex-column detail-panel-col">
            <Card className="mc-panel-card">
              <Card.Body>
                {selectedCell ? (
                  <CellPanel
                    key={selectedCell.element_id}
                    cell={selectedCell}
                    missionId={selMissionId}
                    client={client}
                    onClose={this.handleClosePanel}
                    onOpen={() => this.handleOpenCell(selectedCell.element_id)}
                    onApplyLocal={onApplyLocal}
                    templates={store ? store.templates : {}}
                    canEdit={editable}
                  />
                ) : selEdge ? (
                  <DependencyPanel
                    key={selectedEdgeId}
                    edge={selEdge}
                    cells={cells}
                    onClose={this.handleClosePanel}
                  />
                ) : (
                  <MissionPanel
                    key={selMissionId}
                    mission={mission}
                    missionId={selMissionId}
                    cells={cells}
                    client={client}
                    onApplyLocal={onApplyLocal}
                    onClose={() => this.setState({ panelHidden: true })}
                    templates={store ? store.templates : {}}
                    canEdit={editable}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
          )}
        </Row>

        {/* Confirming (and then watching) a cell delete that takes threads and artifacts with it.
            Mounted on the surface rather than on the node that starts it, because the node is
            removed by the very action the dialog is reporting on. */}
        {deleting && (
          <DeleteCellDialog
            show
            title={deleting.title}
            summary={deleting.summary}
            progress={deleting.progress}
            error={deleting.error}
            onConfirm={this._deleteCellWithObjects}
            onHide={() => this.setState({ deleting: null })}
          />
        )}
      </Container>
    );
  }
}

export default CellsSurface;
